<template>
  <div class="tab-content">
    <div class="block-3">
      <section class="webs-table-wrap">
        <question-table
          :questions="questionList"
          :sort-by="meta.sortBy"
          :sort-order="meta.sortOrder"
          :count-unresolved="countUnresolved"
          :is-loading="uiFlags.isFetching"
          :categories="filterCategoryList"
          :selected-category="selectedCategory"
          @on-update-sortby="onUpdateSortby"
          @on-update-filter-category="onUpdateFilterCategory"
          @on-delete-click="onDeleteClick"
          @on-toggle-enabled-click="onOnToggleEnabledClick"
          @show-edit-question="onOpenEditQuestion"
        />
        <woot-loading-state
          v-if="uiFlags.isFetching"
          :message="$t('QUESTION_SOURCES.TABLE.LOADING')"
        />
        <div
          v-else-if="!uiFlags.isFetching && meta.totalEntries === 0"
          class="webs--empty-records"
        >
          <spinner />
          <span>{{ $t("QUESTION_SOURCES.TABLE.EMPTY_STATE") }}</span>
        </div>

        <table-footer
          :current-page="Number(meta.currentPage)"
          :total-count="meta.totalEntries"
          :page-size="meta.perPage"
          @page-change="onPageChange"
        />
      </section>
    </div>

    <woot-modal :show.sync="showEditQuestion" :on-close="onHideEditQuestion">
      <edit-question
        :row-data="this.loadRowData"
        :categories="categories"
        @close="onHideEditQuestion"
        @submit="onSubmitEditQuestion"
      />
    </woot-modal>

    <woot-delete-modal
      :show.sync="showDeletePopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('QUESTION_SOURCES.ACTIONS.DELETE.CONFIRM.TITLE')"
      :message="$t('QUESTION_SOURCES.ACTIONS.DELETE.CONFIRM.MESSAGE')"
      :confirm-text="$t('QUESTION_SOURCES.ACTIONS.DELETE.CONFIRM.YES')"
      :reject-text="$t('QUESTION_SOURCES.ACTIONS.DELETE.CONFIRM.NO')"
    />
  </div>
</template>

<script>
import { mixin as clickaway } from "vue-clickaway";
import { VeTable } from "vue-easytable";
import { mapGetters } from "vuex";
import Spinner from "shared/components/Spinner.vue";
import timeMixin from "dashboard/mixins/time";
import rtlMixin from "shared/mixins/rtlMixin";
import { formatDateString } from "shared/helpers/DateHelper";
import { WEB_SOURCES_STATES_COLOR } from "../constants";
import Switch from "dashboard/components/ui/Switch.vue";
import TableFooter from 'dashboard/components/widgets/TableFooter';
import globalConfigMixin from 'shared/mixins/globalConfigMixin';
import alertMixin from 'shared/mixins/alertMixin';
import QuestionTable from '../components/QuestionTable.vue';
import EditQuestion from '../components/editQuestion/index.vue';

const DEFAULT_PAGE = 1;

export default {
  components: {
    Spinner,
    VeTable,
    Switch,
    TableFooter,
    QuestionTable,
    EditQuestion,
  },
  mixins: [clickaway, timeMixin, rtlMixin, globalConfigMixin, alertMixin],
  props: {
    webs: {
      type: Array,
      default: () => [],
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showDeletePopup: false,
      showAddPopup: false,
      currentQuestion: null,
      filterCategoryList: [],
      selectedCategory: {},
      showEditQuestion: false,
      loadRowData: {},
      checkboxOption: {
        selectedRowChange: ({ row, isSelected, selectedRowKeys }) => {
          this.selectRows(selectedRowKeys);
        },
        selectedAllChange: ({ isSelected, selectedRowKeys }) => {
          this.selectRows(selectedRowKeys);
        },
      },
    };
  },
  computed: {
    ...mapGetters({
      questionList: "webs/getWebQuestions",
      uiFlags: "webs/getUIFlags",
      meta: 'webs/getWebQuestionsMeta',
      categories: 'questionCategories/getCategories',
      countUnresolved: 'webs/getCountQuestionsUnresolved',
    }),

    pageParameter() {
      const selectedPageNumber = Number(this.$route.query?.page);
      return !Number.isNaN(selectedPageNumber) &&
        selectedPageNumber >= DEFAULT_PAGE
        ? selectedPageNumber
        : DEFAULT_PAGE;
    },

    columns() {
      return [
        {
          key: "check",
          type: "checkbox",
          title: "",
          width: 30,
          align: "center",
        },
        {
          field: "question",
          key: "question",
          title: this.$t("WEB_SOURCES.WEB_DETAIL.TABLE_HEAD.TITLE_QUESTION"),
          fixed: "left",
          width: 250,
          align: this.isRTLView ? "right" : "left",
          renderBodyCell: ({ row }) => {
            return (
              <div class="text-truncate">
                <p class="text-truncate text-title">{row.node.question}</p>
                <p class="text-desc text-title">{row.node.answer}</p>
              </div>
            );
          },
        },
        {
          field: "updated_at",
          key: "updated",
          title: this.$t("WEB_SOURCES.WEB_DETAIL.TABLE_HEAD.LAST_EDITED"),
          align: this.isRTLView ? "right" : "left",
          width: 80,
          renderBodyCell: ({ row }) => {
            return <span>{formatDateString(row.updated_at)}</span>;
          },
        },
        {
          field: "actions",
          key: "actions",
          title: "",
          align: this.isRTLView ? "right" : "left",
          width: 50,
          renderBodyCell: ({ row }) => {
            return (
              <div class="button-wrapper">
                <woot-button
                  v-tooltip={this.$t('QUESTION_SOURCES.ACTIONS.EDIT.BUTTON_TEXT')}
                  variant="smooth"
                  size="tiny"
                  color-scheme="secondary"
                  class-names="grey-btn"
                  icon="edit"
                  onClick={() => this.onOpenEditQuestion(row)}
                />
                <woot-button
                  v-tooltip={this.$t("QUESTION_SOURCES.ACTIONS.DELETE.BUTTON_TEXT")}
                  variant="smooth"
                  size="tiny"
                  color-scheme="alert"
                  class-names="grey-btn"
                  icon="dismiss-circle"
                  onClick={() => this.openDeletePopup(row)}
                />
              </div>
            );
          },
        },
      ];
    },
  },
  async mounted() {
    this.fetchWebQuestions(this.pageParameter);

    await this.$store.dispatch('questionCategories/get');

    this.filterCategoryList = [
      {
        name: 'All Categories',
        question_count: this.categories
          .map(item => item.question_count)
          .reduce((prev, next) => prev + next),
      },
      ...this.categories,
    ];
    this.selectedCategory = this.filterCategoryList[0];
  },
  methods: {
    onSubmitEditQuestion() {
      this.fetchWebQuestions(this.pageParameter);
    },
    onHideEditQuestion() {
      this.showEditQuestion = false;
    },
    onOpenEditQuestion(rowData) {
      this.loadRowData = {
        id: rowData.id,
        question: rowData.node.question,
        response: rowData.node.answer,
        category: ''
      };
      this.showEditQuestion = true;
    },
    onDeleteClick(question) {
      this.currentQuestion = question;
      this.showDeletePopup = true;
    },
    onUpdateFilterCategory(data) {
      this.selectedCategory = data;
      let filter = '';
      if (this.selectedCategory.id) {
        filter = this.selectedCategory.id;
      }
      let page = this.meta.currentPage;
      if (filter !== this.meta.filter) {
        page = DEFAULT_PAGE;
      }
      this.fetchWebQuestions(page, this.meta.sortBy, this.meta.sortOrder, filter);
    },
    onUpdateSortby(sortBy, sortOrder) {
      let page = this.meta.currentPage;
      if (sortBy !== this.meta.sortBy || sortOrder !== this.meta.sortOrder) {
        page = DEFAULT_PAGE;
      }
      this.fetchWebQuestions(page, sortBy, sortOrder);
    },
    selectRows(data) {
      this.$emit('selectrows', data);
    },
    updatePageParam(page) {
      window.history.pushState({}, null, `${this.$route.path}?page=${page}`);
    },

    fetchWebQuestions(page, sortBy, sortOrder, filter) {
      this.updatePageParam(page);

      const { webId } = this.$route.params;
      const requestParams = {
        websiteId: webId,
        page,
        perPage: this.meta.perPage,
        sortBy: sortBy ?? this.meta.sortBy,
        sortOrder: sortOrder ?? this.meta.sortOrder,
        filter: filter ?? this.meta.filter,
      };

      this.$store.dispatch('webs/getWebQuestions', requestParams)
    },

    onPageChange(page) {
      this.fetchWebQuestions(page);
    },

    openDeletePopup(question) {
      this.currentQuestion = question;
      this.showDeletePopup = true;
    },
    closeDeletePopup() {
      this.currentQuestion = null;
      this.showDeletePopup = false;
    },
    async confirmDeletion () {
      const { webId } = this.$route.params;
      await this.$store.dispatch('webs/deleteWebQuestion', { website_id: webId, website_question_id: this.currentQuestion.id });
      this.closeDeletePopup();
      this.showAlert(this.$t('QUESTION_SOURCES.ACTIONS.DELETE.API.SUCCESS_MESSAGE'));
    },
    async onOnToggleEnabledClick(question) {
      try {
        await this.$store.dispatch('questions/toggleEnabled', question);

        this.fetchWebQuestions(this.pageParameter);

        this.showAlert(
          this.$t('QUESTION_SOURCES.ACTIONS.TOGGLE_ENABLED.API.SUCCESS_MESSAGE')
        );

      } catch (error) {
        this.showAlert(
          this.$t('QUESTION_SOURCES.ACTIONS.TOGGLE_ENABLED.API.ERROR_MESSAGE')
        );
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "~dashboard/assets/scss/mixins";
.tab-content {
  padding: 16px;
  background: var(--s-25);
  height: 100%;
  .block-1 {
    border-radius: 6px;
    border: 1px solid #e4ebf1;
    background: #fff;
    display: flex;
    justify-content: space-between;
    padding: 10px 12px;
    align-items: center;
    margin-bottom: 12px;
    p {
      margin-bottom: 0;
    }
    .count-url {
      padding-left: 12px;
      padding-right: 16px;
      border-right: 1px solid #e4ebf1;
      font-weight: 600;
      line-height: 0;
    }
    .text-border {
      font-size: 12px;
      text-transform: unset;
      padding-left: 16px;
      border-left: 1px solid #E4EBF1;
    }
  }
  .block-2 {
    .search-input {
      position: relative;
    }
    .search-icon {
      position: absolute;
      left: 10px;
      top: 10px;
    }
    input {
      padding-left: 30px;
      border-radius: 5px;
      background: #f1f5f8;
      color: #3c4858;
      font-size: 12px;
      border: none;
    }
  }
}
.webs-table-wrap {
  flex: 1 1;
  height: 100%;
}

.webs-table-wrap::v-deep {
  .ve-table {
    padding-top: var(--space-two);
  }
  .ve-table-container {
    overflow-y: initial;
    border: 0 !important;
  }

  .ve-table-header-th {
    padding: var(--space-small) var(--space-two) !important;
  }

  .ve-table-body {
    overflow: auto;
  }
  .ve-table-body-td {
    padding: var(--space-small) var(--space-two) !important;
    background-color: var(--s-25) !important;
    vertical-align: top;
    border: 0 !important;
  }

  .ve-table-header-th {
    font-size: var(--font-size-mini) !important;
    background-color: var(--s-25) !important;
  }
}

.webs--empty-records {
  align-items: center;
  background-color: var(--white);
  border: 1px solid var(--color-border);
  border-top: 0;
  display: flex;
  font-size: var(--font-size-mini);
  height: 7rem;
  justify-content: center;
  margin-top: -1px;
  width: 100%;
}

.web-left-wrap {
  display: flex;
  flex-direction: column;
  flex: 0 0 auto;
  margin-left: var(--space-large);
  margin-right: var(--space-large);
}

.item-center {
  align-items: center;
}
.btn-indicator {
  background-color: unset !important;
}
.btn-indicator:hover,
.btn-indicator:focus {
  background-color: unset !important;
}
.btn-status {
  padding: var(--space-smaller) 1em !important;
  background: #d7f4d8;
  color: #30b537;
}
.text-title {
  color: var(--color-body);
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  margin-bottom: 8px;
}

.text-desc {
  font-weight: 400;
  white-space: break-spaces;
}
a {
  text-decoration-line: underline;
  font-size: 12px;
  color: var(--color-body);
  line-height: 18px;
}
.state-status {
  display: flex;
  gap: 8px;
}
.button.grey-btn {
  margin-left: 0;
  margin-top: 4px;
}
.status-label {
  padding: 3px 10px;
  border-radius: 4px;
  background: #e4ebf1;
  font-size: 12px;
  font-weight: 600;
  line-height: 18px;
  color: #3c4858;
}
.label-completed {
  background: #d7f4d8;
  color: #30b537;
}
</style>

<style lang="scss">
  .webs-page {
    .question-left-wrap {
      margin: 0 !important;
      padding: 0;

      .question-left-header {
        display: none;
      }

      header {
        margin-top: 0;
      }
    }
  }
</style>
